import React, { useEffect, useState } from "react";
import { Printer, ArrowLeft, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import ForaLogo from "./ForaLogo";

const REPORT_INDEX = [
    { path: "/operations", label: "Operations" },
    { path: "/business", label: "Business" },
    { path: "/product-it", label: "Product & IT" },
];

/**
 * Shared shell for all progress reports (Product & IT, Business, Operations).
 * Wraps the report content with: top nav, skip link, print handler, badge
 * removal, ClickUp iframe print fix, and footer.
 *
 * Props:
 *  - children:        the report sections (hero + numbered sections)
 *  - navItems:        [{ href: "#summary", label: "Summary" }, ...]
 *  - lastUpdated:     "May 8, 2026"
 *  - hubLink:         show "Back to hub" pill in top-left (default true)
 */
const ReportLayout = ({ children, navItems = [], lastUpdated, hubLink = true, reportType = "default" }) => {
    const location = useLocation();
    const currentIdx = REPORT_INDEX.findIndex((r) => r.path === location.pathname);
    const prevReport = currentIdx > 0 ? REPORT_INDEX[currentIdx - 1] : null;
    const nextReport =
        currentIdx >= 0 && currentIdx < REPORT_INDEX.length - 1
            ? REPORT_INDEX[currentIdx + 1]
            : null;
    const showSiblings = currentIdx >= 0;
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Remove the platform "Made with Emergent" badge from the DOM (its inline
    // !important styles can't be overridden via CSS).
    useEffect(() => {
        const badge = document.getElementById("emergent-badge");
        if (badge) badge.remove();
    }, []);

    // Hide the ClickUp iframe during print — cross-origin iframes can stall
    // the print pipeline in some browsers. Restore after print.
    useEffect(() => {
        const before = () => {
            document
                .querySelectorAll("iframe.clickup-embed")
                .forEach((f) => {
                    f.dataset._origDisplay = f.style.display || "";
                    f.style.display = "none";
                });
        };
        const after = () => {
            document
                .querySelectorAll("iframe.clickup-embed")
                .forEach((f) => {
                    f.style.display = f.dataset._origDisplay || "";
                });
        };
        window.addEventListener("beforeprint", before);
        window.addEventListener("afterprint", after);
        return () => {
            window.removeEventListener("beforeprint", before);
            window.removeEventListener("afterprint", after);
        };
    }, []);

    const handlePrint = () => {
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
        setTimeout(() => {
            try {
                window.print();
            } catch (e) {
                console.error("Print failed:", e);
            }
        }, 50);
    };

    return (
        <div
            className="min-h-screen bg-white text-[var(--fora-ink)]"
            data-testid="report-page"
            data-report={reportType}
        >
            {/* Skip to content link for screen readers */}
            <a
                href="#summary"
                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-[var(--fora-ink)] focus:text-white focus:px-4 focus:py-2 focus:font-semibold no-print"
                data-testid="skip-link"
            >
                Skip to content
            </a>

            {/* TOP NAV */}
            <header
                className={`no-print sticky top-0 z-40 transition-all ${
                    scrolled
                        ? "bg-white/95 backdrop-blur border-b border-[var(--fora-gray)]"
                        : "bg-white border-b border-transparent"
                }`}
                data-testid="top-nav"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {hubLink && (
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[var(--fora-muted)] hover:text-[var(--fora-red-1)] font-semibold transition-colors"
                                data-testid="nav-hub-link"
                                aria-label="Back to Hub"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Hub</span>
                            </Link>
                        )}
                        <a
                            href="#top"
                            className="flex items-center gap-3"
                            data-testid="nav-logo"
                        >
                            <ForaLogo variant="image" size="1.75rem" />
                            <span className="hidden sm:inline-block text-[11px] tracking-[0.22em] uppercase text-[var(--fora-muted)] font-semibold border-l border-[var(--fora-gray)] pl-3">
                                Solutions
                            </span>
                        </a>
                    </div>
                    {navItems.length > 0 && (
                        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                            {navItems.map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    className="fx-link text-[var(--fora-ink)]/80 hover:text-[var(--fora-ink)]"
                                    data-testid={`nav-${item.href.replace(
                                        "#",
                                        "",
                                    )}`}
                                >
                                    {item.label}
                                </a>
                            ))}
                        </nav>
                    )}
                    <Button
                        onClick={handlePrint}
                        className="bg-[var(--fora-ink)] hover:bg-[var(--fora-red-1)] text-white rounded-none font-[600] tracking-[0.04em] px-5 h-10"
                        data-testid="print-button"
                    >
                        <Printer className="w-4 h-4 mr-2" />
                        Print / PDF
                    </Button>
                </div>
            </header>

            {/* SISTER REPORTS PILL BAR */}
            {showSiblings && (
                <div
                    className="no-print bg-black/95 border-b border-white/10"
                    data-testid="sister-reports-bar"
                >
                    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold hidden sm:block">
                            Other reports
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap flex-1 sm:flex-none sm:justify-end">
                            {REPORT_INDEX.map((r) => {
                                const active = r.path === location.pathname;
                                return (
                                    <Link
                                        key={r.path}
                                        to={r.path}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-bold transition-all ${
                                            active
                                                ? "bg-[var(--fora-red-1)] text-white pointer-events-none"
                                                : "bg-white/5 text-white/65 border border-white/15 hover:bg-white/10 hover:text-white hover:border-white/30"
                                        }`}
                                        data-testid={`sister-report-${r.path.replace("/", "")}`}
                                    >
                                        {active && (
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-white" />
                                        )}
                                        {r.label}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {children}

            {/* CROSS-REPORT FOOTER NAV (prev/next sibling reports) */}
            {showSiblings && (prevReport || nextReport) && (
                <div
                    className="no-print bg-[var(--fora-gray)]/40 border-t border-[var(--fora-gray)]"
                    data-testid="cross-report-nav"
                >
                    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 gap-6">
                        <div>
                            {prevReport && (
                                <Link
                                    to={prevReport.path}
                                    className="group inline-flex items-center gap-3 text-[var(--fora-ink)] hover:text-[var(--fora-red-1)] transition-colors"
                                    data-testid="cross-nav-prev"
                                >
                                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                    <div>
                                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                            Previous report
                                        </div>
                                        <div className="text-base font-[700] tracking-[-0.01em]">
                                            {prevReport.label}
                                        </div>
                                    </div>
                                </Link>
                            )}
                        </div>
                        <div className="text-right">
                            {nextReport && (
                                <Link
                                    to={nextReport.path}
                                    className="group inline-flex items-center gap-3 text-[var(--fora-ink)] hover:text-[var(--fora-red-1)] transition-colors"
                                    data-testid="cross-nav-next"
                                >
                                    <div className="text-right">
                                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                            Next report
                                        </div>
                                        <div className="text-base font-[700] tracking-[-0.01em]">
                                            {nextReport.label}
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* FOOTER */}
            <footer
                className="bg-black text-white/65 border-t border-white/10"
                data-testid="footer"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
                        <div className="flex items-center gap-3 shrink-0">
                            <ForaLogo
                                variant="wordmark"
                                size="1.5rem"
                                color="white"
                            />
                            <span className="text-[11px] tracking-[0.22em] uppercase border-l border-white/15 pl-3">
                                Solutions
                            </span>
                        </div>
                        <div className="text-xs leading-relaxed space-y-2 lg:text-right">
                            <div data-testid="footer-master-info">
                                <span className="text-white font-semibold">
                                    Master F&amp;B d.o.o.
                                </span>
                                {"  |  "}Vlaška ulica 79, 10000 Zagreb, Croatia
                                {"  |  "}Email:{" "}
                                <a
                                    href="mailto:info@master-fb.com"
                                    className="hover:text-[var(--fora-red-1)] transition-colors"
                                >
                                    info@master-fb.com
                                </a>
                            </div>
                            <div className="text-white/50">
                                MBS: 081326069{"  |  "}OIB (VAT ID):
                                27008878697{"  |  "}Registered at the Commercial
                                Court in Zagreb.
                            </div>
                            <div className="text-xs text-white/40 tracking-[0.04em]">
                                FORA is a brand by Master F&amp;B{"  |  "}
                                FORA · Progress Report · Confidential — Internal use only
                            </div>
                            {lastUpdated && (
                                <div
                                    className="text-[10px] tracking-[0.22em] uppercase text-[var(--fora-red-1)] font-semibold pt-1"
                                    data-testid="footer-last-updated"
                                >
                                    Last updated · {lastUpdated}
                                </div>
                            )}
                            <div
                                className="hidden print:block text-[9px] tracking-[0.18em] uppercase text-white/45 font-semibold pt-1"
                                data-testid="footer-pdf-url"
                            >
                                Latest version · progress.master-fb.com
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ReportLayout;
